import React, { useContext, useState, useCallback } from "react";
import { Divider } from "@mui/material";
import { BlobServiceClient, AnonymousCredential, ContainerClient } from "@azure/storage-blob";
import { AZURE_BLOB_URL } from "common/constants";
import { v4 as uuidv4 } from "uuid";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { DropzoneComponent } from "./components/dropzone";
import { useForceIngestTableMutation } from "graphql/types/graphql";
import { context } from "utils/Provider";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CreateDatasetForm from "./components/createDataSetForm";
import DatasetsCard from "./components/dataSetCard";
import pxToRem from "assets/theme/functions/pxToRem";

function Upload(): JSX.Element {
  const { selectedCube, user, setIsLoading } = useContext(context);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState<string | null>(null);
  const [forceIngestTable, { loading }] = useForceIngestTableMutation();

  const handleFileUpload = async () => {
    setIsLoading(true);
    setIsMutating(true);
    const uploadingSnackbarKey = enqueueSnackbar("Uploading file...", { variant: "info", persist: true });
    try {
      const newFileName = uuidv4() + "." + fileToUpload.name.split(".").pop();
      await uploadFileToBlob(fileToUpload, newFileName);

      const success = await forceIngest(newFileName);
      if (!success) {
        enqueueSnackbar("Uploaded data doesn't match dataset", { variant: "error" });
        return;
      }

      enqueueSnackbar("Successful import", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("An error occurred while uploading the file", { variant: "error" });
    } finally {
      setIsLoading(false);
      setFileToUpload(null);
      setSelectedSheetName(null);
      closeSnackbar(uploadingSnackbarKey);
      setIsMutating(false);
    }
  };

  const forceIngest = async (newFileName: string) => {
    if (!datasetId) {
      enqueueSnackbar("Dataset ID is required to force ingest", { variant: "error" });
      return;
    }
    const fileNameWithSheet = selectedSheetName ? `${fileToUpload!.name}_${selectedSheetName}` : fileToUpload!.name;

    const result = await forceIngestTable({
      variables: {
        datasetId,
        cubeId: selectedCube.cubeId,
        fileName: fileNameWithSheet,
        tableUuid: newFileName,
      },
      update: (cache) => {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "dataFromTablesByDatasetId",
        });
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "datasetPreview",
        });
      },
    });

    return result.data.forceIngestTable;
  };

  const sanitizeContainerName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const uploadFileToBlob = useCallback(
    async (file: File, newFileName: string) => {
      try {
        const azureToken = user.azure_sas_token;
        if (!azureToken) {
          throw new Error("Azure SAS token is not available");
        }
        const blobServiceClient = new BlobServiceClient(`${AZURE_BLOB_URL}?${azureToken}`, new AnonymousCredential());

        const containerClient: ContainerClient = blobServiceClient.getContainerClient(
          sanitizeContainerName(selectedCube.name)
        );

        const blobClient = containerClient.getBlockBlobClient(newFileName);
        const options = { blobHTTPHeaders: { blobContentType: file.type } };

        await blobClient.uploadData(file, options);
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : "Something went wrong", { variant: "error" });
      }
    },
    [selectedCube.name, user.azure_sas_token]
  );

  return (
    <MDBox
      sx={{
        borderRadius: "0",
        boxShadow: "none",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        padding: "1rem",
      }}
    >
      <CreateDatasetForm
        onDatasetCreated={() => {
          setDatasetId(null);
        }}
        disabled={isMutating}
      />
      <Divider sx={{ my: pxToRem(11) }} />
      <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
        <MDBox sx={{ flex: 2, overflow: "auto" }}>
          <DatasetsCard
            disableDeleteButton={isMutating}
            setDatasetId={setDatasetId}
            selectedDatasetId={datasetId}
            dropzone={
              <DropzoneComponent
                buttonDisabled={!datasetId || isMutating}
                isMutating={isMutating}
                setIsMutating={setIsMutating}
                fileToUpload={fileToUpload}
                setFileToUpload={setFileToUpload}
                setSelectedSheetName={setSelectedSheetName}
                mutationParam={"azure"}
              />
            }
            importButton={
              <MDButton
                variant="background"
                disabled={!datasetId || isMutating || !fileToUpload}
                sx={{ borderRadius: "8px" }}
                onClick={async () => {
                  await handleFileUpload();
                }}
              >
                Import
              </MDButton>
            }
          />
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 2 }} />
    </MDBox>
  );
}

export default Upload;
